import { Fragment } from 'react';
import {
  Content,
  ContentHeader,
  CreateButton,
  PageWithHeader,
  SupportButton,
  TableColumn,
  TableProps,
} from '@backstage/core-components';
import { configApiRef, useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  CatalogTable,
  catalogPlugin,
  type CatalogTableRow,
} from '@backstage/plugin-catalog';
import {
  CatalogFilterLayout,
  EntityKindPicker,
  EntityLifecyclePicker,
  EntityListPagination,
  EntityListProvider,
  EntityOwnerPicker,
  EntityTagPicker,
  EntityTypePicker,
  UserListFilterKind,
  UserListPicker,
} from '@backstage/plugin-catalog-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { usePermission } from '@backstage/plugin-permission-react';

const defaultColumns: TableColumn<CatalogTableRow>[] = [
  CatalogTable.columns.createTitleColumn({ hidden: true }),
  CatalogTable.columns.createNameColumn({ defaultKind: 'Resource' }),
  CatalogTable.columns.createSystemColumn(),
  CatalogTable.columns.createOwnerColumn(),
  CatalogTable.columns.createSpecTypeColumn(),
  CatalogTable.columns.createSpecLifecycleColumn(),
  CatalogTable.columns.createMetadataDescriptionColumn(),
  CatalogTable.columns.createTagsColumn(),
];

type ResourceExplorerPageContentProps = {
  initiallySelectedFilter?: UserListFilterKind;
  columns?: TableColumn<CatalogTableRow>[] | undefined;
  actions?: TableProps<CatalogTableRow>['actions'];
  ownerPickerMode?: 'owners-only' | 'all';
  pagination?: EntityListPagination;
};

function ResourceExplorerPageContent(props: ResourceExplorerPageContentProps) {
  const {
    initiallySelectedFilter,
    columns,
    actions,
    ownerPickerMode,
    pagination,
  } = props;

  return (
    <EntityListProvider pagination={pagination}>
      <CatalogFilterLayout>
        <CatalogFilterLayout.Filters>
          <EntityKindPicker initialFilter="resource" hidden />
          <EntityTypePicker />
          <UserListPicker initialFilter={initiallySelectedFilter} />
          <EntityOwnerPicker mode={ownerPickerMode} />
          <EntityLifecyclePicker />
          <EntityTagPicker />
        </CatalogFilterLayout.Filters>
        <CatalogFilterLayout.Content>
          <CatalogTable columns={columns ?? defaultColumns} actions={actions} />
        </CatalogFilterLayout.Content>
      </CatalogFilterLayout>
    </EntityListProvider>
  );
}

export type ResourceExplorerPageProps = ResourceExplorerPageContentProps;

/**
 * Catalog explorer focused on Resource entities, using the same filter layout
 * and table columns pattern as the API explorer.
 *
 * @param props - Same optional tuning knobs as the API explorer content layer
 * @returns Page root element
 */
export function ResourceExplorerPage(props: ResourceExplorerPageProps) {
  const {
    initiallySelectedFilter = 'all',
    columns,
    actions,
    ownerPickerMode,
    pagination,
  } = props;

  const configApi = useApi(configApiRef);
  const orgName =
    configApi.getOptionalString('organization.name') ?? 'Backstage';

  const createComponentLink = useRouteRef(
    catalogPlugin.externalRoutes.createComponent,
  );

  const { allowed } = usePermission({
    permission: catalogEntityCreatePermission,
  });

  const headerActions = (
    <Fragment>
      {allowed && (
        <CreateButton
          title="Register existing entity"
          to={createComponentLink?.()}
        />
      )}
      <SupportButton>
        Documentation on registering entities in the catalog
      </SupportButton>
    </Fragment>
  );

  return (
    <PageWithHeader
      themeId="apis"
      title="Resources"
      subtitle={`${orgName} resources registered in the catalog`}
      pageTitleOverride="Resources"
    >
      <Content>
        <ContentHeader title="">{headerActions}</ContentHeader>
        <ResourceExplorerPageContent
          initiallySelectedFilter={initiallySelectedFilter}
          columns={columns}
          actions={actions}
          ownerPickerMode={ownerPickerMode}
          pagination={pagination}
        />
      </Content>
    </PageWithHeader>
  );
}
